import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    BasePageLayout,
    BaseBox,
    BaseTypography,
} from "../../Common/UI/exports";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { fetchUsers, createUser, updateUser, deleteUser } from "../../APIs/userManagementAPI";

import EditIcon from "@mui/icons-material/Edit";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Tooltip } from "@mui/material";

// Simple in-memory cache
const cache = {
    users: null,
};

// Dropdown options
const USER_TYPES = [
    "General User",
    "Super User",
    "Admin",
    "Super Admin",
    "Developer",
    "Tester",
    "Devops",
];

function UserManagement() {
    const email = localStorage.getItem("email");
    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [filterText, setFilterText] = useState("");

    // Dialog state (reused for create + edit)
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState("create"); // 'create' | 'edit'
    const [selectedUser, setSelectedUser] = useState(null);
    const [saving, setSaving] = useState(false);

    // Form inputs
    const [fullNameInput, setFullNameInput] = useState("");
    const [emailInput, setEmailInput] = useState("");
    const [passwordInput, setPasswordInput] = useState("");
    const [typeInput, setTypeInput] = useState(USER_TYPES[0]); // default to first

    // Delete confirmation dialog state
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const handleFilterChange = (e) => setFilterText(e.target.value);

    // Helpers
    const stripWrappingQuotes = (s) =>
        typeof s === "string" ? s.replace(/^"(.*)"$/, "$1") : s;

    // Normalize users to a consistent shape
    const normalizeUser = (u) => {
        if (!u || typeof u !== "object") {
            return {
                id: undefined,
                fullName: "",
                email: stripWrappingQuotes(String(u ?? "")),
                type: USER_TYPES[0],
            };
        }
        return {
            ...u,
            fullName: stripWrappingQuotes(u.fullName ?? u.name ?? u.username ?? ""),
            email: u.email ?? "",
            type: u.type ?? USER_TYPES[0],
        };
    };

    // simple email format check (optional, basic)
    const isValidEmail = (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

    // Prevent duplicates (by email)
    const emailExists = (emailVal, ignoreId) => {
        const e = (emailVal || "").trim().toLowerCase();
        if (!e) return false;
        return users.some((u) => {
            const uu = normalizeUser(u);
            if (ignoreId && uu.id === ignoreId) return false;
            return (uu.email || "").toLowerCase() === e;
        });
    };

    // Open dialog in Create mode
    const handleOpenCreate = () => {
        setDialogMode("create");
        setSelectedUser(null);
        setFullNameInput("");
        setEmailInput("");
        setPasswordInput("");
        setTypeInput(USER_TYPES[0]);
        setIsDialogOpen(true);
    };

    // Open dialog in Edit mode
    const handleOpenEdit = (user) => {
        const normalized = normalizeUser(user);
        setDialogMode("edit");
        setSelectedUser(normalized);
        setFullNameInput(normalized.fullName || "");
        setEmailInput(normalized.email || "");
        setPasswordInput(""); // leave empty; only send if changed
        setTypeInput(normalized.type || USER_TYPES[0]);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        if (saving) return;
        setIsDialogOpen(false);
        setSelectedUser(null);
        setFullNameInput("");
        setEmailInput("");
        setPasswordInput("");
        setTypeInput(USER_TYPES[0]);
        setDialogMode("create");
    };

    // Save handler (create or edit)
    const handleSave = async () => {
        const fullName = stripWrappingQuotes(fullNameInput.trim());
        const emailVal = emailInput.trim();
        const password = passwordInput; // do not trim passwords
        const type = typeInput;

        if (!fullName) {
            toast.warn("Please enter full name.");
            return;
        }
        if (!emailVal) {
            toast.warn("Please enter email.");
            return;
        }
        if (!isValidEmail(emailVal)) {
            toast.warn("Please enter a valid email address.");
            return;
        }
        if (!type) {
            toast.warn("Please select a user type.");
            return;
        }

        if (dialogMode === "create") {
            if (!password) {
                toast.warn("Please enter a password.");
                return;
            }
            if (emailExists(emailVal)) {
                toast.warn(`Email "${emailVal}" is already used.`);
                return;
            }
        } else {
            const ignoreId = selectedUser?.id;
            if (emailExists(emailVal, ignoreId)) {
                toast.warn(`Email "${emailVal}" is already used.`);
                return;
            }
        }

        try {
            setSaving(true);
            if (dialogMode === "create") {
                const payload = { fullName, email: emailVal, password, type };
                const created = await createUser(payload);
                const normalizedCreated = normalizeUser(created);
                const updatedUsers = [...users, normalizedCreated];
                setUsers(updatedUsers);
                cache.users = updatedUsers;
                toast.success(`User "${fullName}" created successfully.`);
                handleCloseDialog();
            } else {
                if (!selectedUser) {
                    toast.error("No user selected for update.");
                    return;
                }

                // Only include password if changed (not empty)
                const payload = { fullName, email: emailVal, type };
                if (password) payload.password = password;

                const updated = await updateUser(selectedUser.id, payload);
                const normalizedUpdated = normalizeUser(updated);

                const finalUpdated =
                    normalizedUpdated && (normalizedUpdated.fullName || normalizedUpdated.email)
                        ? normalizedUpdated
                        : { ...selectedUser, fullName, email: emailVal, type };

                const updatedUsers = users.map((u) => {
                    const uu = normalizeUser(u);
                    if (uu.id != null && finalUpdated.id != null) {
                        return uu.id === finalUpdated.id ? finalUpdated : uu;
                    }
                    // Fallback: match by email if IDs missing (unlikely)
                    return uu.email === selectedUser.email ? finalUpdated : uu;
                });

                setUsers(updatedUsers);
                cache.users = updatedUsers;
                toast.success(`User "${fullName}" updated successfully.`);
                handleCloseDialog();
            }
        } catch (error) {
            console.error("Error saving user:", error);
            toast.error(dialogMode === "create" ? "Failed to create user." : "Failed to update user.");
        } finally {
            setSaving(false);
        }
    };

    const handleOpenDelete = (user) => {
        setUserToDelete(user);
        setIsDeleteOpen(true);
    };

    const handleCloseDelete = () => {
        if (deleting) return;
        setIsDeleteOpen(false);
        setUserToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete?.id) {
            toast.error("Invalid user selected for deletion.");
            return;
        }

        try {
            setDeleting(true);

            // Call API
            await deleteUser(userToDelete.id);

            // Update UI state + cache
            const updatedUsers = users.filter((u) => normalizeUser(u).id !== userToDelete.id);
            setUsers(updatedUsers);
            cache.users = updatedUsers;

            toast.success(`User "${userToDelete.email}" deleted successfully.`);
            handleCloseDelete();
        } catch (error) {
            console.error("Error deleting user:", error);
            toast.error("Failed to delete user.");
        } finally {
            setDeleting(false);
        }
    };

    // Filter ONLY by email (since table shows email)
    const filteredUsers = users
        .map(normalizeUser)
        .filter((u) => {
            const term = filterText.toLowerCase();
            return (
                (u.email || "").toLowerCase().includes(term) ||
                (u.type || "").toLowerCase().includes(term)
            );
        });

    useEffect(() => {
        const loadUsers = async () => {
            if (cache.users) {
                setUsers(cache.users.map(normalizeUser));
            } else {
                try {
                    const data = await fetchUsers();
                    const normalized = Array.isArray(data) ? data.map(normalizeUser) : [];
                    setUsers(normalized);
                    cache.users = normalized;
                } catch (error) {
                    console.error("Error loading Users:", error);
                    toast.error("Failed to load Users.");
                }
            }
        };
        loadUsers();
    }, []);

    return (
        <BasePageLayout sidebarItems={[]}>
            <ToastContainer position="top-right" autoClose={3000} />
            <BaseBox
                sx={{
                    height: "calc(100vh - 140px)",
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: "1160px",
                    mx: "auto",
                    mt: 0,
                    px: 2,
                }}
            >
                <BaseTypography sx={{ fontSize: "1.2rem", mb: 2, fontWeight: 600 }}>
                    All Users
                </BaseTypography>

                {/* Filter & Create */}
                <BaseBox
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    {/* Row 1: Filter input (left) + Create button (right) */}
                    <BaseBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            width: "100%",
                        }}
                    >
                        <TextField
                            size="small"
                            placeholder="Filter by email..."
                            value={filterText}
                            onChange={handleFilterChange}
                            sx={{
                                minWidth: 300,
                                "& .MuiInputBase-root": {
                                    height: 32,
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                            aria-label="Create New User"
                            sx={{ whiteSpace: "nowrap", height: 32, minHeight: 32 }}
                        >
                            Create New User
                        </Button>
                    </BaseBox>
                </BaseBox>

                {/* Users Table (only Email + Action) */}
                <BaseBox
                    sx={{
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        height: "auto",
                        alignSelf: "flex-start",
                        maxHeight: "400px",
                        overflowY: "auto",
                    }}
                >
                    <table className="users-roles-table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Type</th>
                                <th>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filteredUsers.length ? (
                                filteredUsers.map((user, index) => {
                                    const u = normalizeUser(user);
                                    const rowKey = u.id ?? `${u.email}-${index}`;

                                    return (
                                        <tr key={rowKey}>
                                            <td className="action-col center" title={u.email}>{u.email}</td>

                                            {/* NEW: Type column */}
                                            <td className="action-col center" title={u.type}>{u.type}</td>

                                            <td className="action-col center">
                                                <Tooltip title="Edit User" arrow>
                                                    <IconButton
                                                        color="primary"
                                                        size="small"
                                                        aria-label={`Edit ${u.email}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenEdit(u);
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                {/* ✅ NEW: Delete icon */}
                                                <Tooltip title="Delete User" arrow>
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        sx={{ ml: 1.5 }}
                                                        aria-label={`Delete ${u.email}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenDelete(u);
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Attach Roles" arrow>
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        sx={{ ml: 1.5 }}
                                                        aria-label={`Attach roles for ${u.email}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate("/user-management/edit-user", {
                                                                state: { email, user: u },
                                                            });
                                                        }}
                                                    >
                                                        <AdminPanelSettingsIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={3} className="empty">
                                        No Users match your filter.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </BaseBox>
            </BaseBox>

            {/* Create / Edit User Dialog */}
            <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                <DialogTitle>
                    {dialogMode === "create" ? "Create New User" : "Update User"}
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Full Name"
                        placeholder="Enter full name"
                        value={fullNameInput}
                        onChange={(e) => setFullNameInput(stripWrappingQuotes(e.target.value))}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSave();
                            }
                        }}
                        disabled={saving}
                        margin="dense"
                    />

                    <TextField
                        fullWidth
                        label="Email"
                        placeholder="Enter email"
                        type="email"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        disabled={saving}
                        margin="dense"
                        error={!!emailInput && !isValidEmail(emailInput)}
                        helperText={
                            !!emailInput && !isValidEmail(emailInput) ? "Invalid email format" : " "
                        }
                    />

                    <TextField
                        fullWidth
                        label={dialogMode === "create" ? "Password" : "Password (leave blank to keep unchanged)"}
                        placeholder={dialogMode === "create" ? "Enter password" : "Leave blank to keep unchanged"}
                        type="password"
                        value={passwordInput}
                        onChange={(e) => setPasswordInput(e.target.value)}
                        disabled={saving}
                        margin="dense"
                    />

                    <TextField
                        select
                        fullWidth
                        label="Type"
                        value={typeInput}
                        onChange={(e) => setTypeInput(e.target.value)}
                        disabled={saving}
                        margin="dense"
                    >
                        {USER_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={saving} variant="outlined">
                        Close
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={
                            !fullNameInput.trim() ||
                            !emailInput.trim() ||
                            (dialogMode === "create" && !passwordInput) ||
                            saving
                        }
                        variant="contained"
                    >
                        {saving ? "Saving..." : dialogMode === "create" ? "Save" : "Update"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ✅ Delete Confirmation Dialog */}
            <Dialog
                open={isDeleteOpen}
                onClose={handleCloseDelete}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent dividers>
                    <BaseTypography sx={{ fontSize: "0.95rem" }}>
                        Are you sure you want to delete this user?
                    </BaseTypography>

                    <BaseTypography sx={{ mt: 1, fontWeight: 600 }}>
                        {userToDelete?.email}
                    </BaseTypography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseDelete} disabled={deleting} variant="outlined">
                        Cancel
                    </Button>

                    <Button
                        onClick={handleConfirmDelete}
                        disabled={deleting}
                        variant="contained"
                        color="error"
                    >
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </BasePageLayout>
    );
}

export default UserManagement;